import { Nav, THEMES } from "@bloomberg/media-ui-nav/client";
import React, { useCallback } from "react";
import { NavProvider, useNav, REGIONS } from "@foundation/nav";
import { useStickyNav } from "./stickyNavHook";
import type { NavTypes } from "@bloomberg/media-ui-nav";
import type { NAV_ENVIRONMENT } from "@foundation/nav/lib/types";
import type { BannerData } from "@bloomberg/media-ui-nav/types/types";
import { IntlProvider } from "react-intl";
import { RoutePathLocale, RoutePathLocaleValue } from "../../lang/i18n";
import { onLocaleError } from "../../lang/errorHandler";

interface NavComponentProps {
    menuItems: NavTypes.MenuItems;
    environment?: string;
    breakingNews?: NavTypes.BannerData;
    countryCode?: string;
    enableSkylight?: boolean;
    mediaManifestBaseUrl?: string;
    locale?: RoutePathLocaleValue;
    region?: REGIONS;
    translatedMessages?: Record<string, string>;
}

function composeBannerData(
    breakingNews?: BannerData,
    mediaManifestBaseUrl = "https://www.bloomberg.com"
): BannerData {
    const { regionalBreakingNewsLiveStream, globalBreakingNewsLiveStream } =
        breakingNews ?? {};
    const globalStreamId = globalBreakingNewsLiveStream?.streamId;
    const regionalStreamId = regionalBreakingNewsLiveStream?.streamId;

    const breakingNewsStreamSrc = `${mediaManifestBaseUrl}/media-manifest/embed/asset/iframe/skylight?id=${
        globalStreamId ?? regionalStreamId
    }&skylightPlayerType=breakingNewsLive&disableCMP=true`;

    const VideoPlayerComponent = (
        <iframe
            title="skylight-breaking-news-player"
            style={{
                aspectRatio: "16 / 9",
                maxWidth: "150px",
                pointerEvents: "none",
            }}
            src={breakingNewsStreamSrc}
        />
    );

    let bannerDataParams: BannerData = {
        ...breakingNews,
    };

    if (globalStreamId) {
        bannerDataParams = {
            ...bannerDataParams,
            globalBreakingNewsLiveStream: {
                ...globalBreakingNewsLiveStream,
                VideoPlayerComponent,
            },
        };
    }

    if (regionalStreamId) {
        bannerDataParams = {
            ...bannerDataParams,
            regionalBreakingNewsLiveStream: {
                ...regionalBreakingNewsLiveStream,
                VideoPlayerComponent,
            },
        };
    }

    return bannerDataParams;
}

const NavContainer = ({
    breakingNews,
    countryCode,
    enableSkylight,
    locale = RoutePathLocale.en,
    mediaManifestBaseUrl,
    menuItems,
    region = REGIONS.US,
}: Omit<NavComponentProps, "environment" | "translatedMessages">) => {
    const {
        user,
        searchClient,
        initializeSignInWithRegUI,
        onSignOut,
        subscriptionUrl,
        upgradeUrl,
    } = useNav();

    const trackingFunction = useCallback(
        (eventAction: string, eventLabel: string) => {
            if (!window.dataLayer) {
                return;
            }

            window.dataLayer.push({
                event: "navClick",
                eventCategory: "nav",
                eventAction,
                eventLabel,
            });
        },
        []
    );

    return (
        <Nav
            theme={THEMES.DARK}
            user={user}
            menuItems={menuItems}
            searchClient={searchClient}
            initializeSignInWithRegUI={initializeSignInWithRegUI}
            onSignOut={onSignOut}
            bannerData={
                enableSkylight
                    ? composeBannerData(breakingNews, mediaManifestBaseUrl)
                    : breakingNews
            }
            subscriptionUrl={subscriptionUrl}
            upgradeUrl={upgradeUrl}
            desktopBreakpointClassName="bb-desktop-new-nav"
            trackingFunction={trackingFunction}
            countryCode={countryCode}
            locale={locale}
            region={region}
        />
    );
};

export default function NavComponent({
    menuItems,
    environment,
    breakingNews,
    countryCode,
    enableSkylight,
    mediaManifestBaseUrl,
    translatedMessages,
    locale = RoutePathLocale.en,
    region = REGIONS.US,
}: NavComponentProps) {
    useStickyNav();

    return (
        <div className="nav">
            <IntlProvider
                locale={locale}
                defaultLocale={RoutePathLocale.en}
                messages={translatedMessages}
                onError={onLocaleError}
            >
                <NavProvider environment={environment as NAV_ENVIRONMENT}>
                    <NavContainer
                        breakingNews={breakingNews}
                        countryCode={countryCode}
                        enableSkylight={enableSkylight}
                        locale={locale}
                        mediaManifestBaseUrl={mediaManifestBaseUrl}
                        menuItems={menuItems}
                        region={region}
                    />
                </NavProvider>
            </IntlProvider>
        </div>
    );
}
